/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CollapsedErrorMessage } from 'src/core/components/CollapsedErrorMessage';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import {
    ProjectCreationSteps,
    ProjectInfoDto,
    projectCreationContentLeftMargin
} from 'src/customText/verticals/projectCreationModal/ProjectCreationModal';
import { ModalFooter } from 'src/customText/verticals/projectCreationModal/components/ModalFooter';

export type ProjectCreationReviewAndFinishProps = {
    onSubmit: () => void;
    onDismiss: () => void;
    onPrevious: () => void;
    projectDto: ProjectInfoDto;
};

const messages = defineMessages({
    title: {
        id: 'customText.reviewAndFinishScreen.title',
        defaultMessage: 'Review and finish'
    },
    description: {
        id: 'customText.reviewAndFinishScreen.description',
        defaultMessage: 'Review the configurations you set for your project in the previous steps.'
    },
    projectType: {
        id: 'customText.reviewAndFinishScreen.projectType',
        defaultMessage: 'Project type'
    },
    azureResource: {
        id: 'customText.reviewAndFinishScreen.azureResource',
        defaultMessage: 'Azure resource'
    },
    projectName: {
        id: 'customText.reviewAndFinishScreen.projectName',
        defaultMessage: 'Project name'
    },
    projectDescription: {
        id: 'customText.reviewAndFinishScreen.projectDescription',
        defaultMessage: 'Description'
    },
    language: {
        id: 'customText.reviewAndFinishScreen.language',
        defaultMessage: 'Language'
    },
    multipleLanguages: {
        id: 'customText.reviewAndFinishScreen.multipleLanguages',
        defaultMessage: 'Multiple languages'
    },
    storageAccount: {
        id: 'customText.reviewAndFinishScreen.storageAccount',
        defaultMessage: 'Storage account'
    },
    blobStorage: {
        id: 'customText.reviewAndFinishScreen.blobStorage',
        defaultMessage: 'Blob store container'
    },
    enabled: {
        id: 'customText.reviewAndFinishScreen.enabled',
        defaultMessage: 'Enabled'
    },
    disabled: {
        id: 'customText.reviewAndFinishScreen.disabled',
        defaultMessage: 'Disabled'
    },
    noValuePlaceholder: {
        id: 'customText.reviewAndFinishScreen.noValuePlaceholder',
        defaultMessage: 'N/A'
    },
    singleClassificationProjectType: {
        id: 'customText.reviewAndFinishScreen.singleClassificationProjectType',
        defaultMessage: 'Single label classification '
    },
    multiClassificationProjectType: {
        id: 'customText.reviewAndFinishScreen.multiClassificationProjectType',
        defaultMessage: 'Multi label classification '
    }
});

const { styleComponent } = getStylistV2('ProjectCreationReviewAndFinish');
const ContentWrapper = styleComponent<IStackProps>(Stack)('ContentWrapper', { overflowY: 'auto', maxHeight: '420px' });

export const ReviewAndFinishScreen = observer((props: ProjectCreationReviewAndFinishProps) => {
    const { onSubmit, onDismiss, onPrevious, projectDto } = props;
    const { localize } = useLocalization();
    const { containerName, storageAccountName, projectDescription, projectName, defaultLanguage, multiLingual, projectKind } = projectDto;
    const { rootStore, dispatcher } = useGlobalState();
    const { csAccountStore } = rootStore.azureStore.session;
    const { customTextCultureStorehouse } = rootStore;
    const projectCreationTracker = useProgressTracker({ onComplete: onSubmit });
    const { isLargeOrGreater } = useResponsiveMode();

    const cultureName = customTextCultureStorehouse.items.find(c => c.code === defaultLanguage).name;

    const isClassificationProject = projectKind !== 'CustomEntityRecognition';
    const isSingleClassificationProject = projectKind === 'CustomSingleLabelClassification';

    const LabeledText = ({ label, text }: { label: string; text: string }) => (
        <Stack tokens={{ childrenGap: 4 }}>
            <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{label}</LuisText>
            <LuisText>{text}</LuisText>
        </Stack>
    );

    const multiLinguality = multiLingual ? localize(messages.enabled) : localize(messages.disabled);

    const handleCreateProject = () => {
        if (projectDto.labelsLocation) {
            dispatcher.dispatch('importCustomTextLabels', {
                toastData: {},
                tracker: projectCreationTracker,
                projectKind: projectDto.projectKind,
                projectName: projectDto.projectName,
                multiLingual: projectDto.multiLingual,
                containerName: projectDto.containerName,
                labelsFileName: projectDto.labelsLocation,
                defaultLanguage: projectDto.defaultLanguage,
                projectDescription: projectDto.projectDescription
            });
        } else {
            dispatcher.dispatch('createCustomTextProject', {
                tracker: projectCreationTracker,
                ...(projectDto as ProjectInfoDto),
                toastData: {}
            });
        }
    };

    return (
        <Stack>
            <Stack
                styles={{ root: { maxHeight: '400px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 24, padding: `${isLargeOrGreater ? `0 0 0 ${projectCreationContentLeftMargin}` : '12px 20px'}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>{localize(messages.description)}</LuisText>
                </Stack>

                <ContentWrapper tokens={{ childrenGap: 16 }}>
                    <LabeledText
                        label={localize(messages.azureResource)}
                        text={csAccountStore.name || localize(messages.noValuePlaceholder)}
                    />
                    <LabeledText label={localize(messages.projectName)} text={projectName || localize(messages.noValuePlaceholder)} />
                    {isClassificationProject && (
                        <LabeledText
                            label={localize(messages.projectType)}
                            text={
                                (isSingleClassificationProject
                                    ? localize(messages.singleClassificationProjectType)
                                    : localize(messages.multiClassificationProjectType)) || localize(messages.noValuePlaceholder)
                            }
                        />
                    )}
                    <LabeledText
                        label={localize(messages.projectDescription)}
                        text={projectDescription || localize(messages.noValuePlaceholder)}
                    />
                    <LabeledText label={localize(messages.language)} text={cultureName || localize(messages.noValuePlaceholder)} />
                    <LabeledText
                        label={localize(messages.multipleLanguages)}
                        text={multiLinguality || localize(messages.noValuePlaceholder)}
                    />
                    <LabeledText
                        label={localize(messages.storageAccount)}
                        text={storageAccountName || localize(messages.noValuePlaceholder)}
                    />
                    <LabeledText label={localize(messages.blobStorage)} text={containerName || localize(messages.noValuePlaceholder)} />

                    {projectCreationTracker.isFailed && <CollapsedErrorMessage error={projectCreationTracker.error} />}
                </ContentWrapper>
            </Stack>

            <ModalFooter
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                activeStep={ProjectCreationSteps.ReviewAndFinish}
                onNext={handleCreateProject}
                isLoading={projectCreationTracker.isRunning}
                isFooterDisabled={projectCreationTracker.isRunning}
            />
        </Stack>
    );
});
